import type { Block, SectionBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { BlockWrapper } from '../BlockWrapper';
import { canvasBlockStyle, cn } from '../../helpers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BlockAdder } from '../BlockAdder';

interface SectionRendererProps {
  block: SectionBlock;
  path: DragPath;

  rawVariableDefinitions: Record<string, unknown> | undefined;
  /** Workspace audience attribute keys, when the document may merge them. */
  audienceAttributeKeys?: readonly string[] | undefined;
}

export function SectionRenderer({
  rawVariableDefinitions,
  audienceAttributeKeys,
  block,
  path,
}: SectionRendererProps) {
  const parent = path.length > 0 ? path[path.length - 1] : null;

  const {
    backgroundType,
    backgroundSrc,
    backgroundPosition,
    backgroundSize,
    backgroundFixed,
    overlayColor,
    minHeight,
    maxWidth,
    verticalAlign,
    paddingX,
    paddingY,
    reference,
    blocks: nestedBlocks,
  } = block.attrs;

  const inner = (nestedBlocks as Block[]).map((b) => (
    <BlockWrapper
      block={b}
      key={b.id}
      path={[...path, { id: block.id, type: block.type }]}
      rawVariableDefinitions={rawVariableDefinitions}
      audienceAttributeKeys={audienceAttributeKeys}
    />
  ));

  // Build background styles based on backgroundType
  const backgroundStyles: React.CSSProperties = { ...canvasBlockStyle(block.style) };
  if (backgroundType === 'image' && backgroundSrc) {
    backgroundStyles.backgroundImage = `url(${backgroundSrc})`;
    backgroundStyles.backgroundPosition = backgroundPosition;
    backgroundStyles.backgroundSize = backgroundSize;
    backgroundStyles.backgroundRepeat = 'no-repeat';
    if (backgroundFixed) {
      backgroundStyles.backgroundAttachment = 'fixed';
    }
  } else if (backgroundType === 'color') {
    // If you support a color attr, wire it here; otherwise fall back to style.background
    backgroundStyles.backgroundColor = block.style?.backgroundColor ?? undefined;
  } else if (backgroundType === 'gradient') {
    // The inspector writes the CSS gradient to style.backgroundGradient;
    // attrs.backgroundSrc is where older documents kept it and still renders.
    // The background colour stays underneath as the solid fallback.
    backgroundStyles.backgroundImage =
      block.style?.backgroundGradient ?? backgroundSrc ?? undefined;
    backgroundStyles.backgroundColor = block.style?.backgroundColor ?? undefined;
  }

  backgroundStyles.borderTopRightRadius = block.style?.borderTopRightRadius;
  backgroundStyles.borderTopLeftRadius = block.style?.borderTopLeftRadius;
  backgroundStyles.borderBottomLeftRadius = block.style?.borderBottomLeftRadius;
  backgroundStyles.borderBottomRightRadius = block.style?.borderBottomRightRadius;

  const verticalAlignMap: Record<string, string> = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  };

  return (
    <div
      id={reference || undefined}
      className={cn(
        'border-line relative flex flex-col overflow-hidden rounded-lg',
        // The dashed outline is the editor's own furniture, so it gives way to a
        // border the author set: two outlines on one box would be a lie about
        // which one the message carries.
        block.style?.border ? '' : 'border border-dashed',
      )}
      style={{
        ...backgroundStyles,
        minHeight: minHeight ?? undefined,
        padding: `${paddingY} ${paddingX}`,
        justifyContent: verticalAlignMap[verticalAlign] ?? 'center',
      }}
    >
      {overlayColor && (
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{ backgroundColor: overlayColor }}
        />
      )}

      <div
        className="relative z-[2] mx-auto w-full space-y-4"
        style={{ maxWidth: maxWidth ?? undefined }}
      >
        <div
          className={cn('z-[2] flex w-full gap-4', {
            'flex-col': block.style?.flexDirection === 'column' || !block.style?.flexDirection,
            'flex-row': block.style?.flexDirection === 'row',
          })}
        >
          <SortableContext
            items={(nestedBlocks as Block[]).map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {inner}
          </SortableContext>
        </div>

        <BlockAdder
          parent={{
            id: block.id,
            type: 'section',
            isSectionInAccordion: parent?.type === 'accordion',
          }}
          path={path}
        />
      </div>
    </div>
  );
}
